// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/useAskV2PlanProgress.test.ts
import { type AskPunttMessage } from "@mg/schemas/src/christo/askPuntt";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  ASK_V2_PLAN_SKIPPED_DETAIL,
  ASK_V2_PLAN_STARTING_ID,
  buildAskV2PlanProgress,
  planProgressTally,
  readLatestPlanStepsFromMessages,
} from "./buildRailProgressFromPlan";
import {
  type AskV2ReviewProgressMode,
  messagesAfterLatestReviewStart,
  messagesOfRunningTurn,
} from "./collectRailContent";
import { type AskV2RailProgressItem } from "./types";

/**
 * The review rail's rows, read off the checklist warhol writes with
 * `write_plan`.
 *
 * The rows used to be a client-side timer script on a three-minute budget, so
 * they advanced whether or not the review did. They now report only what the
 * model put in its own plan, which is why a step can sit `pending` for a long
 * time: that is the review's real position.
 *
 * The rows are derived from persisted messages, so a reload or a second tab
 * rebuilds the same rail instead of restarting it -- the old script kept its
 * clock in per-tab `sessionStorage` and began again from the first step.
 *
 * The plan's OWN steps are the whole list, and the "k/n completed" line is
 * counted off those same steps. The per-check detail rows and the verifier
 * tally that #5685 nested under them were removed: the rail's job is the 4-6
 * step summary the model wrote, and the checks behind those steps are
 * per-tool-call detail, which the thread draws against the call that ran it.
 */
export function useAskV2PlanProgress(input: {
  messages?: readonly AskPunttMessage[];
  mode: AskV2ReviewProgressMode;
}): {
  items: AskV2RailProgressItem[];
  showShell: boolean;
} {
  const { t } = useTranslation("ui");
  const { messages, mode } = input;

  const cycle = useMemo(
    () => messagesAfterLatestReviewStart(messages ?? []),
    [messages],
  );

  const turn = useMemo(() => {
    const all = messages ?? [];
    const running = messagesOfRunningTurn(all);
    if (!running?.[0]) return null;

    // A stale placeholder from an earlier review can survive in overlaid
    // history briefly. Never let that older turn cross the latest Start review
    // boundary and revive its plan over the new review.
    const latestStartIndex = all.length - cycle.length - 1;
    const turnStartIndex = all.indexOf(running[0]);
    return turnStartIndex >= latestStartIndex ? running : null;
  }, [cycle, messages]);
  const turnRunning = turn !== null;

  // A live turn is narrower than a review cycle. This prevents a completed
  // review's plan from flashing over a follow-up that has not planned yet.
  const scoped = turn ?? cycle;

  const steps = useMemo(
    () => readLatestPlanStepsFromMessages(scoped),
    [scoped],
  );

  const items = useMemo(() => {
    if (mode === "idle") return [];
    // The "k/n completed" line, counted off the same steps the rows come from
    // so the two cannot disagree. Built here, where `t` lives, because the
    // builder is pure and i18n-free.
    const tally = planProgressTally(steps);
    const planned = buildAskV2PlanProgress({
      steps,
      // Review-cycle status can become completed while its active turn still
      // runs. Preserve the plan's own statuses until that turn settles.
      completed: mode === "completed" && !turnRunning,
      ...(tally
        ? {
            progressDetail: t("askV2.rail.progressSteps.completed", {
              completed: tally.completed,
              total: tally.total,
            }),
          }
        : {}),
    });
    if (planned.length > 0) {
      // The builder is pure and i18n-free, so the skipped row arrives marked.
      // Translate it here, where `t` lives, or the rail renders untranslated
      // English in every locale but English.
      return planned.map((item) =>
        item.detail === ASK_V2_PLAN_SKIPPED_DETAIL
          ? { ...item, detail: t("askV2.rail.progressSteps.skipped") }
          : item,
      );
    }
    if (mode !== "running" && !turnRunning) return [];
    // No plan yet. A turn that needs one tool call never writes one, and a
    // planned turn is still deciding, so this is the only row that may be
    // shown without the backend having reported it -- and it claims nothing
    // but that the turn has started.
    return [
      {
        id: ASK_V2_PLAN_STARTING_ID,
        label: t("askV2.rail.progressSteps.starting"),
        status: "active" as const,
      },
    ];
  }, [mode, steps, t, turnRunning]);

  return { items, showShell: mode !== "idle" };
}
